<script setup lang="ts">
    import StemPanel from '@/views/3d-planner/stem/StemPanel.vue';
    import PlannerStatus from '@/views/3d-planner/components/PlannerStatus.vue';
    import DeveloperTools from '@/views/3d-planner/components/developerTools/DeveloperTools.vue';
    import PlannerSyncError from '@/views/3d-planner/components/PlannerSyncError.vue';
    import { Form } from 'vee-validate';
    import CupPanel from '@/views/3d-planner/cup/CupPanel.vue';
    import ApprovePlan from '@/views/3d-planner/components/ApprovePlan.vue';
    import HipSpineAnalysisPanel from '@/views/3d-planner/hipSpine/components/HipSpineAnalysisPanel.vue';
    import { usePlannerSidebarViewModel } from './usePlannerSidebarViewModel';

    const vm = usePlannerSidebarViewModel();
</script>

<template>
    <!--
        Note: Using mobile: false
        If false, the component will be displayed in desktop mode always.
        This prevents the drawer from resizing when the app is in mobile (smaller media queries).
    -->
    <v-navigation-drawer
        absolute
        class="side-panel-container-drawer"
        color="grey-transparent"
        :mobile="false"
        data-testid="planner-sidebar-open"
    >
        <!-- fill-height passes the drawer's definite height down so that
             .side-panel-container's `height: 100%` resolves properly.
             Without it, this wrapper is auto-height, content overflows the
             drawer, and v-navigation-drawer__content's built-in overflow-y
             scrolls everything (including the X and the approve footer). -->
        <div class="d-flex position-relative fill-height">
            <v-sheet class="side-panel-container d-flex flex-column pr-0 mr-4">
                <div
                    class="d-flex pt-0 align-center ml-0 text-uppercase text-paragraph-sm text-secondary-text"
                >
                    <div v-if="vm.devToolsSelected">Dev tools</div>
                    <div v-else-if="vm.expandedPanel === 'stem'" class="my-2 pt-2">Stem</div>
                    <div v-else-if="vm.expandedPanel === 'cup'" class="my-2 pt-2">Cup</div>

                    <v-spacer />
                    <span class="position-absolute right-0 top-0 mt-3 mr-2">
                        <v-hover>
                            <template #default="{ isHovering, props }">
                                <v-btn
                                    v-bind="props"
                                    elevation="0"
                                    icon
                                    size="x-small"
                                    height="35"
                                    width="35"
                                    :base-color="isHovering ? 'surface-light' : 'white'"
                                    @click.stop="vm.closeSidebar()"
                                >
                                    <v-icon size="20" color="dark-blue">mdi-close</v-icon>
                                </v-btn>
                            </template>
                        </v-hover>
                    </span>
                </div>

                <planner-status class="planner-status" />

                <v-overlay
                    v-model="vm.disabled"
                    contained
                    scrim="white"
                    opacity="0.5"
                    :aria-disabled="true"
                />

                <planner-sync-error />

                <v-container class="side-panel-container-form pb-0 px-0 pt-2">
                    <Form v-slot="{ meta }" class="fill-height d-flex flex-column w-100">
                        <v-container fluid class="side-panel-container-form-scrollable px-0 pa-0">
                            <developer-tools
                                v-if="vm.devToolsSelected && vm.developerToolsEnabled"
                            />
                            <stem-panel v-else-if="vm.expandedPanel === 'stem'" />
                            <cup-panel v-else-if="vm.expandedPanel === 'cup'" />
                        </v-container>
                        <div
                            v-if="!vm.devToolsSelected && vm.expandedPanel !== null"
                            class="approve-plan-footer"
                        >
                            <approve-plan :valid="meta.valid" />
                        </div>
                    </Form>
                </v-container>
            </v-sheet>

            <hip-spine-analysis-panel v-if="vm.hipSpineAnalysisVisible" />
        </div>
    </v-navigation-drawer>
</template>

<!--suppress CssUnusedSymbol -->
<style scoped lang="scss">
    .side-panel-container-drawer {
        width: fit-content !important;
        border-radius: 0 8px 8px 0;
    }

    .v-navigation-drawer.bg-grey-transparent {
        background-color: rgba(248, 248, 248, 0.87);
    }
    
    .side-panel-container {
        padding: 0 12px;
        background: none;
        max-width: 1000px;
        height: 100%;
        z-index: 20;
        // Pair with `d-flex flex-column` on the template: explicit so the
        // column layout below is intentional, not accidental.
        display: flex;
        flex-direction: column;

        .side-panel-container-form {
            // flex: 1 + min-height: 0 lets the form absorb the leftover
            // space inside the drawer (after the title row), and crucially
            // lets it shrink below its intrinsic content height so the
            // scrollable child below can take over scrolling.
            flex: 1;
            min-height: 0;
            border-radius: 12px;

            .side-panel-container-form-scrollable {
                display: flex;
                min-width: 400px;
                width: fit-content;
                gap: 8px;
                // Same pattern: absorb the leftover form-height (minus the
                // approve-plan-footer below) and scroll its own content
                // when it would otherwise overflow. The drawer itself
                // never needs to scroll, so the X stays at the top.
                flex: 1;
                min-height: 0;
                overflow-y: auto;
            }
        }
    }

    .approve-plan-footer {
        position: relative;
        top: -1vh;
        width: 400px;
        background-color: rgb(var(--v-theme-surface-light));
        border-radius: 0 0 12px 12px;
        padding: 0 16px 16px;
        flex-shrink: 0;
    }

    .planner-status {
        position: absolute;
        right: 60px;
        align-items: center;
        top: 20px;
        z-index: 3000;
    }
</style>
